// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
import { CouponForm } from '~/components/module/setting';

// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_COUPON,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { ICoupon } from '~/utils/interface/coupon';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { deleteCoupon, getListCoupon } from '~/thunks/coupon/couponThunk';
import { couponActions } from '~/thunks/coupon/couponSlice';
import { formatDate } from '~/utils/helper/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { COUPON_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './Coupons.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsCoupon = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<ICoupon>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{record?.name}</Box>,
    },
    {
      id: 'discountRate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_value')}</Box>
          <SortComponent name='discount rate' sortType={getSortType('discount rate')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{record?.discountRate}</Box>,
    },
    {
      id: 'discountType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_unit')}</Box>
          <SortComponent name='discount type' sortType={getSortType('discount type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{record?.discountType}</Box>,
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_quantity')}</Box>
          <SortComponent name='quantity' sortType={getSortType('quantity')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{record?.quantity || EMPTY_VALUE}</Box>,
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_start_date')}</Box>
          <SortComponent name='start date' sortType={getSortType('start date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{formatDate(record?.startDate)}</Box>,
    },
    {
      id: 'endDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_end_date')}</Box>
          <SortComponent name='end date' sortType={getSortType('end date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => <Box className={cx('textValue')}>{formatDate(record?.endDate)}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ICoupon) => (
        <Box className={cx('textValue')}>
          {record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE}
        </Box>
      ),
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: ICoupon) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Coupons = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateCoupons, canViewCoupons, canEditCoupons, canDeleteCoupons } = usePermissions(COUPON_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/coupon', 'common/base']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isLoading, isRefreshCouponList } = useAppSelector((state) => state.coupon);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_COUPON);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isOpenCouponFormModal, setIsOpenCouponFormModal] = useState<boolean>(false);
  const [idCoupon, setIdCoupon] = useState<string>(EMPTY_STRING);
  const [couponList, setCouponList] = useState<ICoupon[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListCoupon(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshCouponList) return;

    handleGetListCoupon(paramObject);

    dispatch(couponActions.setRefreshCouponList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshCouponList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListCoupon = (params: IQueryParamBase) => {
    dispatch(getListCoupon(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setCouponList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleConfirmDelete = () => {
    dispatch(deleteCoupon(idCoupon))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        handleCloseConfirmDelete();
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleShowCouponFormModal = () => {
    setIsOpenCouponFormModal((prev) => !prev);
    setIdCoupon(EMPTY_STRING);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleCloseConfirmDelete = () => {
    setIdCoupon(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdCoupon(id);
    setIsOpenCouponFormModal(true);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdCoupon(id);
    setIsShowConfirmDelete(true);
  };
  //#endregion Handle Function

  return (
    <Box id='couponsPage' className={cx('container')}>
      <BaseToolbar title={t('coupon_title')} isBack description={t('coupon_sub_title')}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateCoupons && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowCouponFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('coupon_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewCoupons ? couponList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsCoupon(t, sortBy, handleSort, handleEdit, handleDelete, canEditCoupons, canDeleteCoupons)}
          onPageChange={handlePageChange}
          {...(canEditCoupons ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isOpenCouponFormModal && <CouponForm idCoupon={idCoupon} onClose={handleShowCouponFormModal} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Coupons;
